import { useState, useEffect } from "react";
import API, { formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Settings, Save, Check, Loader2, Info, Palette, Type, ImagePlus, Trash2, Plus } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [tierImgBusy, setTierImgBusy] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/admin/loyalty-config");
        setConfig(data);
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  const uploadTierImage = async (tierName, file) => {
    if (!file) return;
    setTierImgBusy(tierName); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await API.post(`/admin/loyalty-config/tier-image?tier=${encodeURIComponent(tierName)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setConfig(c => ({ ...c, tiers: (c.tiers || []).map(t => (t.name || "").toLowerCase() === tierName.toLowerCase() ? { ...t, card_image_url: data.url } : t) }));
    } catch (err) { setError(formatError(err.response?.data?.detail) || "Image upload failed"); }
    finally { setTierImgBusy(""); }
  };

  const removeTierImage = async (tierName) => {
    setTierImgBusy(tierName);
    try {
      await API.delete(`/admin/loyalty-config/tier-image?tier=${encodeURIComponent(tierName)}`);
      setConfig(c => ({ ...c, tiers: (c.tiers || []).map(t => (t.name || "").toLowerCase() === tierName.toLowerCase() ? { ...t, card_image_url: "" } : t) }));
    } catch { /* ignore */ } finally { setTierImgBusy(""); }
  };

  const setTierField = (i, field, value) => {
    setConfig(c => { const tiers = [...c.tiers]; tiers[i] = { ...tiers[i], [field]: value }; return { ...c, tiers }; });
  };

  const addTier = () => {
    setConfig(c => ({ ...c, tiers: [...(c.tiers || []), { name: "", min_points: 0, max_points: 999999, bonus: 0, open_percent: null, close_percent: null, card_image_url: "", color_primary: "", color_accent: "", color_text: "" }] }));
  };

  const removeTier = (i) => {
    setConfig(c => ({ ...c, tiers: (c.tiers || []).filter((_, idx) => idx !== i) }));
  };

  const setFont = (key, value) => {
    setConfig(c => ({ ...c, fonts: { ...(c.fonts || {}), [key]: value === "" ? null : parseInt(value) || null } }));
  };

  const handleSave = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      // Per-tier Open % / Close % are the only earning rates now (blank = 0%).
      await API.put("/admin/loyalty-config", {
        registration_bonus: config.registration_bonus,
        first_scan_bonus: config.first_scan_bonus,
        min_redemption_points: config.min_redemption_points,
        scan_radius_meters: config.scan_radius_meters,
        tiers: config.tiers,
        fonts: config.fonts || {},
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#008A99]" /></div>;
  if (!config) return <div className="text-center py-20 text-slate-400">Failed to load config</div>;

  return (
    <div className="space-y-6 max-w-5xl animate-slide-up" data-testid="admin-config">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Loyalty Configuration</h2>
        <p className="text-sm text-gray-500 mt-0.5">Manage points earning rules and tier thresholds</p>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>}
      {saved && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-600 text-sm flex items-center gap-2"><Check className="w-4 h-4" /> Configuration saved</div>}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
        <h3 className="font-semibold text-[#0F172A] flex items-center gap-2" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}><Settings className="w-5 h-5 text-[#008A99]" /> Bonuses & Redemption</h3>
        <p className="text-xs text-slate-500 -mt-2">Earning percentages are configured per tier below — leave a tier&apos;s Open % / Close % blank to award 0 points for that reagent type.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Registration Bonus</label>
            <Input data-testid="config-reg-bonus" type="number" value={config.registration_bonus} onChange={(e) => setConfig(c => ({ ...c, registration_bonus: parseInt(e.target.value) || 0 }))} className="h-10 rounded-xl" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">First Scan Bonus</label>
            <Input data-testid="config-scan-bonus" type="number" value={config.first_scan_bonus} onChange={(e) => setConfig(c => ({ ...c, first_scan_bonus: parseInt(e.target.value) || 0 }))} className="h-10 rounded-xl" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Min Redemption Pts</label>
            <Input data-testid="config-min-redeem" type="number" value={config.min_redemption_points} onChange={(e) => setConfig(c => ({ ...c, min_redemption_points: parseInt(e.target.value) || 0 }))} className="h-10 rounded-xl" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Scan Radius (m)</label>
            <Input data-testid="config-scan-radius" type="number" min="0" value={config.scan_radius_meters ?? 150} onChange={(e) => setConfig(c => ({ ...c, scan_radius_meters: parseInt(e.target.value) || 0 }))} className="h-10 rounded-xl" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
        <h3 className="font-semibold text-[#0F172A]" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Tier Configuration</h3>
        <p className="text-xs text-slate-500 -mt-2">Set per-tier earning % for Open/Close reagents. <span className="font-semibold text-amber-700">Blank = 0% (no points awarded).</span></p>
        <div className="bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 text-[11px] text-sky-800 leading-relaxed" data-testid="tier-config-open-close-hint">
          <p><span className="font-bold">Close %</span> applies to <span className="font-semibold">System Pack Reagents</span> only.</p>
          <p><span className="font-bold">Open %</span> applies to <span className="font-semibold">Open Reagents, QC &amp; Calibrators, ELISA, and Rapids</span>.</p>
        </div>
        <div className="hidden md:grid grid-cols-7 gap-3 text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-1">
          <span>Tier Name</span>
          <span>Min Points</span>
          <span>Max Points</span>
          <span>Upgrade Bonus</span>
          <span>Open %</span>
          <span>Close %</span>
          <span></span>
        </div>
        {(config.tiers || []).map((tier, i) => (
          <div key={i} className="grid grid-cols-2 md:grid-cols-7 gap-3 items-end pb-3 border-b border-slate-100 last:border-0" data-testid={`tier-config-${i}`}>
            <div>
              <label className="text-[10px] text-slate-500 mb-1 block md:hidden">Tier Name</label>
              <Input data-testid={`tier-name-${i}`} value={tier.name || ""} onChange={(e) => {
                const tiers = [...config.tiers];
                tiers[i] = { ...tiers[i], name: e.target.value };
                setConfig(c => ({ ...c, tiers }));
              }} className="h-9 rounded-lg" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 mb-1 block md:hidden">Min Points</label>
              <Input type="number" value={tier.min_points} onChange={(e) => {
                const tiers = [...config.tiers];
                tiers[i] = { ...tiers[i], min_points: parseInt(e.target.value) || 0 };
                setConfig(c => ({ ...c, tiers }));
              }} className="h-9 rounded-lg" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 mb-1 block md:hidden">Max Points</label>
              <Input type="number" value={tier.max_points} onChange={(e) => {
                const tiers = [...config.tiers];
                tiers[i] = { ...tiers[i], max_points: parseInt(e.target.value) || 0 };
                setConfig(c => ({ ...c, tiers }));
              }} className="h-9 rounded-lg" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 mb-1 block md:hidden">Upgrade Bonus</label>
              <Input type="number" value={tier.bonus} onChange={(e) => {
                const tiers = [...config.tiers];
                tiers[i] = { ...tiers[i], bonus: parseInt(e.target.value) || 0 };
                setConfig(c => ({ ...c, tiers }));
              }} className="h-9 rounded-lg" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 mb-1 block md:hidden">Open %</label>
              <Input data-testid={`tier-open-${i}`} type="number" step="0.1" value={tier.open_percent ?? ""} placeholder="—" onChange={(e) => {
                const tiers = [...config.tiers];
                const v = e.target.value;
                tiers[i] = { ...tiers[i], open_percent: v === "" ? null : parseFloat(v) };
                setConfig(c => ({ ...c, tiers }));
              }} className="h-9 rounded-lg" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 mb-1 block md:hidden">Close %</label>
              <Input data-testid={`tier-close-${i}`} type="number" step="0.1" value={tier.close_percent ?? ""} placeholder="—" onChange={(e) => {
                const tiers = [...config.tiers];
                const v = e.target.value;
                tiers[i] = { ...tiers[i], close_percent: v === "" ? null : parseFloat(v) };
                setConfig(c => ({ ...c, tiers }));
              }} className="h-9 rounded-lg" />
            </div>
            <div className="flex md:justify-center md:items-end">
              <button type="button" onClick={() => removeTier(i)} className="text-red-400 hover:text-red-600 p-2" data-testid={`tier-remove-${i}`} title="Remove tier">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        <button type="button" onClick={addTier} className="flex items-center gap-2 text-sm font-semibold text-[#008A99] hover:text-[#006670]" data-testid="add-tier-btn">
          <Plus className="w-4 h-4" /> Add Tier
        </button>
      </div>

      {/* Point 16: Tier card designs — background image + colours per tier */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4" data-testid="tier-design-section">
        <h3 className="font-semibold text-[#0F172A] flex items-center gap-2" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}><Palette className="w-5 h-5 text-[#008A99]" /> Tier Card Designs</h3>
        <p className="text-xs text-slate-500 -mt-2">Upload a card background image and set the text/accent colours shown on each customer&apos;s loyalty card. Leave blank to use the default design.</p>
        <div className="grid md:grid-cols-3 gap-4">
          {(config.tiers || []).map((tier, i) => {
            const name = tier.name || `Tier ${i + 1}`;
            const img = tier.card_image_url ? (tier.card_image_url.startsWith("http") ? tier.card_image_url : `${BACKEND}${tier.card_image_url}`) : "";
            return (
              <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-3" data-testid={`tier-design-${i}`}>
                <p className="font-semibold text-sm text-slate-800">{name}</p>
                <div className="aspect-[1.586] w-full rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                  {img ? <img src={img} alt={name} className="w-full h-full object-cover" data-testid={`tier-design-preview-${i}`} /> : <span className="text-[11px] text-slate-400">Default design</span>}
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex-1 cursor-pointer">
                    <span className="flex items-center justify-center gap-1.5 h-8 rounded-lg bg-[#008A99]/10 text-[#008A99] text-xs font-medium hover:bg-[#008A99]/20 transition-colors">
                      {tierImgBusy === name ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />} Upload
                    </span>
                    <input type="file" accept="image/*" className="hidden" data-testid={`tier-design-upload-${i}`} onChange={(e) => { uploadTierImage(name, e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                  {img && <button onClick={() => removeTierImage(name)} className="h-8 px-2 rounded-lg text-red-500 hover:bg-red-50" data-testid={`tier-design-remove-${i}`}><Trash2 className="w-4 h-4" /></button>}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[["color_text", "Text"], ["color_accent", "Accent"], ["color_primary", "Primary"]].map(([field, lbl]) => (
                    <div key={field}>
                      <label className="text-[10px] text-slate-500 mb-1 block">{lbl}</label>
                      <input type="color" value={tier[field] || "#0a2540"} onChange={(e) => setTierField(i, field, e.target.value)}
                        className="w-full h-8 rounded-lg border border-slate-200 cursor-pointer p-0.5" data-testid={`tier-${field}-${i}`} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Point 17: Customer-app font sizes */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4" data-testid="font-size-section">
        <h3 className="font-semibold text-[#0F172A] flex items-center gap-2" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}><Type className="w-5 h-5 text-[#008A99]" /> Customer App Font Sizes</h3>
        <p className="text-xs text-slate-500 -mt-2">Set the font sizes (in pixels) used across the customer app. Leave blank to keep the default sizes.</p>
        <div className="grid grid-cols-3 gap-4">
          {[["heading", "Heading", "e.g. 24", 12, 48], ["subheading", "Sub-heading", "e.g. 18", 10, 36], ["body", "Body / Content", "e.g. 14", 8, 28]].map(([key, lbl, ph, mn, mx]) => (
            <div key={key}>
              <label className="text-xs text-slate-500 mb-1 block">{lbl} (px)</label>
              <Input type="number" min={mn} max={mx} placeholder={ph} value={(config.fonts || {})[key] ?? ""} onChange={(e) => setFont(key, e.target.value)} className="h-10 rounded-xl" data-testid={`font-${key}-input`} />
            </div>
          ))}
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} className="w-full h-12 rounded-xl bg-[#008A99] hover:bg-[#006670] text-white font-medium" data-testid="save-config-button">
        <Save className="w-4 h-4 mr-2" />
        {saving ? "Saving..." : "Save Configuration"}
      </Button>

      <AboutEditor />
    </div>
  );
}

/** Admin editor for the customer-facing "About Us" section (heading + body). */
function AboutEditor() {
  const [heading, setHeading] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get("/admin/about-content");
        setHeading(data?.heading || "");
        setBodyText((data?.body || []).join("\n\n"));
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      const body = bodyText.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
      await API.put("/admin/about-content", { heading: heading.trim(), body });
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4" data-testid="admin-about-editor">
      <h3 className="font-semibold text-[#0F172A] flex items-center gap-2" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>
        <Info className="w-5 h-5 text-[#008A99]" /> About Us Section
      </h3>
      <p className="text-xs text-slate-500 -mt-2">Shown on the customer home screen. Separate paragraphs with a blank line — the last paragraph renders as a closing tagline.</p>
      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>}
      {saved && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-600 text-sm flex items-center gap-2"><Check className="w-4 h-4" /> About Us saved</div>}
      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#008A99]" /></div>
      ) : (
        <>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Heading</label>
            <Input data-testid="about-heading-input" value={heading} onChange={(e) => setHeading(e.target.value)} className="h-10 rounded-xl" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Body (one paragraph per block, separated by a blank line)</label>
            <Textarea data-testid="about-body-input" value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={10} className="rounded-xl resize-y" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full h-11 rounded-xl bg-[#008A99] hover:bg-[#006670] text-white font-medium" data-testid="save-about-button">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save About Us"}
          </Button>
        </>
      )}
    </div>
  );
}
